import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router';
import type { ProjectFileResponseDto } from '@inference.engine/workspace-api-client/web';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import { SETTLED_EXIT_SECONDS } from '@/shared/ui/animations';
import {
  EditFileTagsModal,
  RenameFileModal,
  useDeleteProjectFile,
} from '@/features/manage-project-files';
import { useProjectCapabilities } from '@/entities/user';
import { notificationService } from '@/lib/services/notification.service';
import { DeleteConfirmationModal } from '@/shared/ui/DeleteConfirmationModal';
import { ROUTES } from '@/shared/config/routes';
import { parseProblemDetail } from '@/shared/utils/problem-details';
import { FileAssetCard, FileAssetCardSkeleton } from '../FileAssetCard';
import { useTranslation } from 'react-i18next';

interface Props {
  projectId: string;
  folderId: string;
  files: ProjectFileResponseDto[];
  isLoading: boolean;
  /** Bulk-selection mode (MFINS-2759) — threaded down to each card. */
  selectionMode?: boolean;
  /** Whether this caller may select at all; without it no checkbox is drawn. */
  canSelect?: boolean;
  selectedIds?: Set<string>;
  onToggleSelect?: (file: ProjectFileResponseDto) => void;
}

/**
 * Cards fill the pane rather than sitting at a minimum width with the slack
 * spent on an extra column. Measured against the prototype: a ~1200px pane
 * draws four cards of roughly 300px, which a 238px track turned into five of
 * 241px. Same track as the subfolder grid above it, so the two read as one
 * surface when a folder holds both.
 */
export const GRID_CLASS = 'grid grid-cols-[repeat(auto-fill,minmax(280px,1fr))] gap-app-inset';

export const SKELETON_CARD_COUNT = 10;
const SKELETON_CARDS = Array.from({ length: SKELETON_CARD_COUNT }, (_, i) => i);

export function AssetGrid(props: Props) {
  const { t } = useTranslation('projects');
  const { t: tCommon } = useTranslation('common');

  const {
    projectId,
    folderId,
    files,
    isLoading,
    selectionMode,
    canSelect,
    selectedIds,
    onToggleSelect,
  } = props;
  const navigate = useNavigate();
  const projects = useProjectCapabilities();
  const shouldReduceMotion = useReducedMotion();

  const [fileToRename, setFileToRename] = useState<ProjectFileResponseDto | null>(null);
  const [fileToTag, setFileToTag] = useState<ProjectFileResponseDto | null>(null);
  const [fileToDelete, setFileToDelete] = useState<ProjectFileResponseDto | null>(null);

  const deleteMutation = useDeleteProjectFile({ projectId });

  const handleCopyLink = useCallback(
    async (file: ProjectFileResponseDto) => {
      const path = ROUTES.PROJECT_FILE_DETAIL(projectId, folderId, file.id);
      try {
        await navigator.clipboard.writeText(`${window.location.origin}${path}`);
        notificationService.showSuccess(t('projectDetail.assetGrid.linkCopied'));
      } catch {
        notificationService.showError(t('projectDetail.assetGrid.failedToCopyLink'));
      }
    },
    [projectId, folderId, t],
  );

  const handleConfirmDelete = () => {
    if (!fileToDelete) return;
    deleteMutation.mutate(fileToDelete.id, {
      onSuccess: () => {
        notificationService.showSuccess(t('projectDetail.assetGrid.fileDeleted'));
        setFileToDelete(null);
      },
      onError: (error) => {
        const { detail } = parseProblemDetail(
          error,
          t('projectDetail.assetGrid.couldNotDeleteFile'),
        );
        notificationService.showError(detail);
      },
    });
  };

  return (
    <>
      {/*
        Nothing here enters. The pane's own skeleton is what arrives; the rows
        are already in place by the time the placeholder over them dissolves,
        so only the leaving half is animated.

        Both halves share one grid cell, and there is deliberately no
        `mode="wait"`: the rows have to be underneath while the skeleton fades,
        or the fade is a gap with nothing behind it.
      */}
      <div className="grid">
        <AnimatePresence initial={false}>
          {isLoading ? (
            <motion.div
              key="loading"
              data-testid="asset-grid-skeleton"
              className={`col-start-1 row-start-1 ${GRID_CLASS}`}
              exit={{ opacity: 0 }}
              transition={{ duration: shouldReduceMotion ? 0 : SETTLED_EXIT_SECONDS }}
            >
              {SKELETON_CARDS.map((i) => (
                <FileAssetCardSkeleton key={i} />
              ))}
            </motion.div>
          ) : (
            <div key="loaded" className={`col-start-1 row-start-1 ${GRID_CLASS}`}>
              {files.map((file) => (
                <FileAssetCard
                  key={file.id}
                  projectId={projectId}
                  folderId={folderId}
                  file={file}
                  onClick={() =>
                    void navigate(ROUTES.PROJECT_FILE_DETAIL(projectId, folderId, file.id))
                  }
                  onRename={setFileToRename}
                  onCopyLink={(targetFile) => void handleCopyLink(targetFile)}
                  onEditTags={setFileToTag}
                  onDelete={setFileToDelete}
                  actionsDisabled={!projects.canUploadAssets}
                  selectionMode={selectionMode}
                  selected={selectedIds?.has(file.id)}
                  onToggleSelect={canSelect ? onToggleSelect : undefined}
                />
              ))}
            </div>
          )}
        </AnimatePresence>
      </div>

      {fileToRename && (
        <RenameFileModal
          open
          projectId={projectId}
          file={fileToRename}
          onClose={() => setFileToRename(null)}
        />
      )}
      {fileToTag && (
        <EditFileTagsModal
          open
          projectId={projectId}
          file={fileToTag}
          onClose={() => setFileToTag(null)}
        />
      )}
      <DeleteConfirmationModal
        open={fileToDelete !== null}
        isDeleting={deleteMutation.isPending}
        resourceType={tCommon('components.deleteConfirmationModal.file')}
        resourceName={fileToDelete?.fileName}
        warningMessage={t('projectDetail.assetGrid.thisFileWillBeMoved')}
        onCancel={() => setFileToDelete(null)}
        onConfirmDelete={handleConfirmDelete}
      />
    </>
  );
}
